/**
 * PACUVitalsEntry.js
 *
 * Single PACU vitals form with Aldrete scoring via radio buttons.
 * Captures vital signs (BP, HR, SpO2, Temp, RR) and five Aldrete
 * sub-scores (Activity, Respiration, Circulation, Consciousness, O2 Sat).
 */
import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TextField from '../../Utils/FormInputs/TextField';
import Label from '../../Utils/FormInputs/Label';

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid #EEEEEE',
    borderRadius: 8,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  vitalsSection: {
    padding: '16px 20px',
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  // ── Aldrete ───────────────────────────────────────
  aldreteWrap: {
    backgroundColor: '#FAFAFA',
    padding: '16px 20px 12px',
    borderTop: '1px solid #EEEEEE',
  },
  aldreteRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '6px 0',
    borderBottom: '1px solid #F0F0F0',
    '&:last-child': { borderBottom: 'none' },
  },
  aldreteLabel: {
    minWidth: 120,
    paddingTop: 8,
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#424242',
  },
  // ── Total score ───────────────────────────────────
  totalStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '10px 0 0',
  },
  totalLabel: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#757575',
  },
  totalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 14px',
    borderRadius: 20,
    fontWeight: 700,
    fontSize: '0.88rem',
    fontFamily: 'monospace',
  },
  badgeRed: { backgroundColor: '#FFEBEE', color: '#C62828' },
  badgeAmber: { backgroundColor: '#FFF8E1', color: '#F57F17' },
  badgeGreen: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
}));

const ALDRETE_CRITERIA = [
  {
    key: 'activity',
    label: 'Activity',
    options: [
      { value: 2, label: 'Moves all 4 extremities' },
      { value: 1, label: 'Moves 2 extremities' },
      { value: 0, label: 'Unable to move' },
    ],
  },
  {
    key: 'respiration',
    label: 'Respiration',
    options: [
      { value: 2, label: 'Deep breath & cough' },
      { value: 1, label: 'Dyspnoea / shallow' },
      { value: 0, label: 'Apnoea' },
    ],
  },
  {
    key: 'circulation',
    label: 'Circulation',
    options: [
      { value: 2, label: 'BP ±20% of pre-op' },
      { value: 1, label: 'BP ±20–50% of pre-op' },
      { value: 0, label: 'BP ±50% of pre-op' },
    ],
  },
  {
    key: 'consciousness',
    label: 'Consciousness',
    options: [
      { value: 2, label: 'Fully awake' },
      { value: 1, label: 'Arousable on calling' },
      { value: 0, label: 'Not responding' },
    ],
  },
  {
    key: 'o2Saturation',
    label: 'O₂ Saturation',
    options: [
      { value: 2, label: '>92% on room air' },
      { value: 1, label: 'Needs O₂ to maintain >90%' },
      { value: 0, label: 'SpO₂ <90% with O₂' },
    ],
  },
];

export { ALDRETE_CRITERIA };

const PACUVitalsEntry = ({ values = {}, onChange, disabled = false }) => {
  const classes = useStyles();

  const handleFieldChange = (e) => {
    if (onChange) onChange(e.target.name, e.target.value);
  };

  const handleAldreteChange = (field) => (e) => {
    if (onChange) onChange(field, parseInt(e.target.value, 10));
  };

  const aldreteTotal = useMemo(() => {
    let sum = 0;
    let allScored = true;
    ALDRETE_CRITERIA.forEach((c) => {
      const val = values[c.key];
      if (val !== undefined && val !== null && val !== '') {
        sum += Number(val);
      } else {
        allScored = false;
      }
    });
    return { sum, allScored };
  }, [values]);

  const getBadgeClass = (score) => {
    if (score < 7) return classes.badgeRed;
    if (score < 9) return classes.badgeAmber;
    return classes.badgeGreen;
  };

  return (
    <div className={classes.root}>
      {/* ── Vital Signs ────────────────────────────── */}
      <div className={classes.vitalsSection}>
        <Typography className={classes.sectionTitle}>Vital Signs</Typography>
        <Row className="mb-2">
          <Col xs={12} md={3}>
            <Form.Group>
              <Label name="Date & Time" type />
              <TextField name="recordedAt" type="datetime-local" value={values.recordedAt || ''} onChange={handleFieldChange} disabled={disabled} />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Systolic" />
              <TextField name="systolic" type="number" value={values.systolic || ''} onChange={handleFieldChange} placeholder="mmHg" disabled={disabled} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Diastolic" />
              <TextField name="diastolic" type="number" value={values.diastolic || ''} onChange={handleFieldChange} placeholder="mmHg" disabled={disabled} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Heart Rate" />
              <TextField name="heartRate" type="number" value={values.heartRate || ''} onChange={handleFieldChange} placeholder="bpm" disabled={disabled} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="SpO2" />
              <TextField name="spo2" type="number" value={values.spo2 || ''} onChange={handleFieldChange} placeholder="%" disabled={disabled} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Temp" />
              <TextField name="temperature" type="number" value={values.temperature || ''} onChange={handleFieldChange} placeholder="°C" disabled={disabled} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Resp. Rate" />
              <TextField name="respiratoryRate" type="number" value={values.respiratoryRate || ''} onChange={handleFieldChange} placeholder="/min" disabled={disabled} />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ── Aldrete Scoring ────────────────────────── */}
      <div className={classes.aldreteWrap}>
        <Typography className={classes.sectionTitle} style={{ borderBottomColor: '#E0E0E0' }}>
          Aldrete Recovery Score
        </Typography>

        {ALDRETE_CRITERIA.map((criterion) => (
          <div className={classes.aldreteRow} key={criterion.key}>
            <FormControl component="fieldset" fullWidth disabled={disabled}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span className={classes.aldreteLabel}>{criterion.label}</span>
                <RadioGroup
                  row
                  value={
                    values[criterion.key] !== undefined && values[criterion.key] !== null
                      ? String(values[criterion.key])
                      : ''
                  }
                  onChange={handleAldreteChange(criterion.key)}
                >
                  {criterion.options.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={String(opt.value)}
                      control={<Radio size="small" color="primary" />}
                      label={
                        <span style={{ fontSize: '0.82rem' }}>
                          {opt.value} &ndash; {opt.label}
                        </span>
                      }
                    />
                  ))}
                </RadioGroup>
              </div>
            </FormControl>
          </div>
        ))}

        {/* Total */}
        <div className={classes.totalStrip}>
          <span className={classes.totalLabel}>Total:</span>
          <span className={`${classes.totalBadge} ${getBadgeClass(aldreteTotal.sum)}`}>
            {aldreteTotal.allScored ? aldreteTotal.sum : `${aldreteTotal.sum}*`} / 10
          </span>
          {!aldreteTotal.allScored && (
            <span style={{ fontSize: '0.72rem', color: '#9E9E9E', fontStyle: 'italic' }}>
              * incomplete
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PACUVitalsEntry;
